/**
 * 행사 시작·시간·종료 입력 보조 — **폼 계산 전용**(표시 포맷은 utils/datetime.ts).
 *
 * 화면 세 곳이 같은 장치를 쓴다: 영상 상세 모달, 영상 업로드 모달, 비로그인 업로드.
 * 세 곳에 각자 복제돼 있던 것을 여기로 모았다 — 규칙이 갈라지면 "같은 값을 넣었는데
 * 화면마다 종료가 다르다"가 되고, 그건 사용자가 원인을 짚을 수 없는 종류의 어긋남이다.
 *
 * ⚠️ **행사 시간은 저장 필드가 아니다.** DB 에 들어가는 건 시작(event_start_at)·
 *    종료(expires_at) 뿐이고, 행사 시간은 그 둘에서 역산해 보여주고 그 둘을 계산해 주는
 *    입력 보조다. 진실원천을 시작·종료로 두는 게 핵심 — 행사 시간을 따로 들고 있으면
 *    반드시 어긋난다.
 */

const pad2 = (n: number) => String(n).padStart(2, '0');

/**
 * datetime-local 입력값의 모양 검사.
 *
 * ⚠️ `new Date('2026-08-')` 는 실패하지 않고 2026-08-01T00:00 으로 파싱된다 — 그대로 쓰면
 *    타이핑 중간값이 조용히 엉뚱한 종료 시각이 된다(FileDetailModal 에서 실측으로 확인).
 */
const LOCAL_INPUT_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/;

/** datetime-local 입력값 → epoch ms. **모양이 완전하지 않으면 NaN.** */
export function parseLocalInput(localInput: string): number {
  if (!LOCAL_INPUT_RE.test(localInput)) return NaN;
  return new Date(localInput).getTime();
}

/** epoch ms → datetime-local 입력값(브라우저 로컬, 분 단위). */
export function toLocalInput(ms: number): string {
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** ISO instant → datetime-local 입력값. 빈 값이면 빈 문자열. */
export function isoToLocalInput(iso?: string | null): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? '' : toLocalInput(t);
}

/** datetime-local 입력값 + N분 → datetime-local 입력값. 계산 불가면 빈 문자열. */
export function localInputPlusMinutes(startLocal: string, minutes: number): string {
  if (!Number.isFinite(minutes)) return '';
  const t = parseLocalInput(startLocal);
  if (!Number.isFinite(t)) return '';
  return toLocalInput(t + minutes * 60_000);
}

/** 두 datetime-local 사이의 분. 계산 불가면 null, 종료가 시작보다 이르면 **음수**. */
export function minutesBetween(startLocal: string, endLocal: string): number | null {
  const s = parseLocalInput(startLocal);
  const e = parseLocalInput(endLocal);
  if (!Number.isFinite(s) || !Number.isFinite(e)) return null;
  return Math.round((e - s) / 60_000);
}

/** 분 → "1시간 30분" 사람용 표기. 0 이하·계산 불가면 null. */
export function humanizeMinutes(min: number | null): string | null {
  if (min === null || !Number.isFinite(min) || min <= 0) return null;
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h && m) return `${h}시간 ${m}분`;
  if (h) return `${h}시간`;
  return `${m}분`;
}

/** 행사 시간 기본값(분) — 종전 "종료 = 행사 시작 + 1시간" 규칙과 같은 값. */
export const DEFAULT_DURATION_MIN = 60;

/**
 * 시작이 바뀔 때의 다음 상태 — **행사 시간을 유지한 채 종료를 옮긴다.**
 * (시작만 미루는 게 흔한 편집이라, 종료를 고정하면 행사 시간이 조용히 줄어든다.)
 * 시작이 비면 종료·행사 시간도 함께 비운다.
 */
export function onStartChanged(
  nextStart: string,
  currentDurationMin: string,
): { start: string; end: string; durationMin: string } {
  if (!nextStart) return { start: '', end: '', durationMin: '' };
  const min = Number(currentDurationMin);
  const useMin = Number.isFinite(min) && min > 0 ? min : DEFAULT_DURATION_MIN;
  return {
    start: nextStart,
    end: localInputPlusMinutes(nextStart, useMin),
    durationMin: String(useMin),
  };
}

/**
 * 행사 시간이 바뀔 때의 종료. 시작이 없거나 값이 비정상이면 **종료를 건드리지 않는다**
 * (타이핑 중간값으로 종료가 튀지 않게) — null 반환은 "그대로 두라"는 뜻.
 */
export function endFromDuration(startLocal: string, durationMin: string): string | null {
  const min = Number(durationMin);
  if (!durationMin || !Number.isFinite(min) || min <= 0) return null;
  if (!startLocal) return null;
  const end = localInputPlusMinutes(startLocal, min);
  return end || null;
}

/**
 * 종료가 바뀔 때의 행사 시간(역산). 종료가 시작보다 이르면 **음수를 그대로 돌려준다** —
 * 타이핑 중간값을 임의로 고치면 입력이 튄다. 경고·저장 차단은 호출부가 `isRangeInvalid` 로.
 */
export function durationFromEnd(startLocal: string, endLocal: string): string {
  const min = minutesBetween(startLocal, endLocal);
  return min === null ? '' : String(min);
}

/**
 * 종료가 시작보다 **이른가**(= 저장 불가). 같은 시각(0분)은 허용 — 스냅의 결과이고
 * "같음"은 "이전"이 아니다.
 */
export function isRangeInvalid(startLocal: string, endLocal: string): boolean {
  return (minutesBetween(startLocal, endLocal) ?? 0) < 0;
}

/**
 * 시작 칸에서 포커스가 빠질 때의 스냅 — 종료가 시작보다 이르면 **시작 + 기본 행사 시간**.
 * 종료를 직접 이르게 고쳐 놓고 시작 칸만 스쳐 지나가면(onChange 미발화) 잘못된 상태가
 * 그대로 남는데, 그 창을 닫는다. 고칠 게 없으면 null.
 *
 * ⚠️ 시작과 '같게'(0분) 맞추지 않는 이유: expires_at == event_start_at 은 "행사가 시작하는
 *    그 순간 만료"라 그 영상은 사실상 재생되지 않는다. 오류를 고친 자리에 함정을 두지 않는다.
 */
export function snapOnStartBlur(
  startLocal: string,
  endLocal: string,
): { end: string; durationMin: string } | null {
  const gap = minutesBetween(startLocal, endLocal);
  if (gap === null || gap >= 0) return null;
  return {
    end: localInputPlusMinutes(startLocal, DEFAULT_DURATION_MIN),
    durationMin: String(DEFAULT_DURATION_MIN),
  };
}
